/**
 * 签约弹窗组件（ContractSignPop，2026-10-30 签约呈现改造批）
 *
 * 两档同一张壳（遮罩＋`.reveal-detail-overlay`/`.reveal-detail-card`，与剧本详情层同族）：
 *  - 重档（heavy）：内容槽＝调用方给的剧本/角色详情 HTML 或节点（直签＝roleCardHtml 那份角色卡；
 *    商务＝丙工位自渲染的品牌内容）。组件不认任何业务数据，只负责卡壳、按钮行与开关。
 *  - 轻档（light）：极简戏票（剧名 → 角色名 → 番位＋竞角状态 → 周期/片酬两读数），5c 竞角揭晓用。
 *
 * **强制二选一（两档同尺，2026-10-30 茄子令）**：点遮罩空白⛔关闭、无 X、无「先放着」、esc 空操作
 * （modal.ts 只认 `.btn-cancel`/`data-esc`，本组件两样都不挂）；唯一出口＝按钮行里的一颗钮。
 * 按钮⛔由组件决定文案与语义——`SignPopButton` 交给调用方（放弃＝ghost、主操作＝pool-cta）。
 *
 * API 契约（丙工位接商务只认这几行）：
 *   const pop = new ContractSignPop(app.overlayRoot);   // 挂在覆盖层宿主（壳），5c 则挂执行页面板根
 *   pop.open({
 *       tier: 'heavy',
 *       content: '<div class="pd-head">…</div>',        // string 或已建好的 HTMLElement，原样塞进 .pop-detail
 *       buttons: [
 *           { label: '放弃', ghost: true, run: () => { … } },
 *           { label: '签约', run: () => { …; return false; } },   // 返回 false＝动作没走成：卡留在原地
 *       ],
 *       onResolved: () => next(),                        // 任一钮动作成功、弹窗已关之后回调一次
 *   });
 *   pop.close();                                         // 宿主切场景时兜底收起
 *
 * 轻档 info：
 *   { script: '千金不换', pos: '女主角', name: '温念初',
 *     status: { text: '竞角成功', ok: true },
 *     stats: [{ label: '周期', value: '6 周' }, { label: '片酬', value: '¥72.0万' }] }
 *   （status 缺省＝不挂状态胶囊；ok=false 走红系）
 */
import { createButton, createDiv, createSpan } from './ui';
import { bindPdFold } from './roleCard';

export interface SignPopButton {
    label: string;
    /** 次要键（放弃）：ghost 细钮，宽度比主键小（沿用重档按钮行 0.55 : 2.2） */
    ghost?: boolean;
    /** 不可点（已签约/已放弃/未解锁/钱不够）：置灰＋禁用 */
    disabled?: boolean;
    /** 置灰但可点（名额满/同剧已签）：点了由 run 给原因 */
    blocked?: boolean;
    /** 已办成态（已签约）：薄荷浅底＋禁用，与 `.pool-cta.done` 同族 */
    done?: boolean;
    /** 原生 title（未解锁/金钱不足这类只能靠悬停说明的置灰态） */
    title?: string;
    /** 动作本体；返回 false＝卡留在原地（调用方已 showHint 说明原因） */
    run?: () => boolean | void;
}

export interface SignPopLightInfo {
    /** 剧名（戏票抬头） */
    script: string;
    /** 番位/角色类型原词（女主角/女二号…）；空串＝不挂（无名角色名占大字位时） */
    pos?: string;
    /** 角色名（主角大字；无名角色可不传） */
    name?: string;
    /** 竞角成败胶囊；不传＝不挂。mute＝灰档「已签他角」（色档见 ui.css `.sign-pop-chip.mute` 注） */
    status?: { text: string; ok: boolean; mute?: boolean };
    /** 两读数（周期/片酬），按传入顺序排一行 */
    stats: { label: string; value: string }[];
}

export type SignPopOptions = {
    buttons: SignPopButton[];
    /** 动作成功并关闭弹窗后回调一次（推进送达链/刷新列表） */
    onResolved?: () => void;
} & (
    | { tier: 'heavy'; content: string | HTMLElement }
    | { tier: 'light'; info: SignPopLightInfo }
);

export class ContractSignPop {
    readonly overlay: HTMLElement;
    private body: HTMLElement;
    private onResolved: (() => void) | null = null;

    /** parent＝挂载父：直签＝app.overlayRoot（弹窗挂壳口径），5c＝月度执行面板根 */
    constructor(parent: HTMLElement) {
        this.overlay = createDiv('dimoverlay reveal-detail-overlay');
        this.body = createDiv('modal-note-card script-detail reveal-detail-card');
        this.overlay.appendChild(this.body);
        parent.appendChild(this.overlay);
        // 点遮罩＝不关闭（强制二选一）；⛔ 冒泡——宿主面板有点屏推进（5c 揭晓层同款拦截）
        this.overlay.addEventListener('click', e => e.stopPropagation());
    }

    get isOpen(): boolean {
        return this.overlay.classList.contains('show');
    }

    open(opts: SignPopOptions): void {
        this.body.innerHTML = '';
        const detail = createDiv('pop-detail');
        if (opts.tier === 'heavy') {
            if (typeof opts.content === 'string') detail.innerHTML = opts.content;
            else detail.appendChild(opts.content);
            bindPdFold(detail);
        } else {
            this.renderLight(detail, opts.info);
        }
        const btnRow = createDiv('btn-row');
        opts.buttons.forEach(b => btnRow.appendChild(this.makeButton(b)));
        detail.appendChild(btnRow);
        this.body.appendChild(detail);
        this.onResolved = opts.onResolved ?? null;
        this.overlay.classList.add('show');
    }

    close(): void {
        this.overlay.classList.remove('show');
        this.onResolved = null;
    }

    /** 运行期换宿主（5c 决策页背景版：面板构造时宿主未定，由 app 注入 shell 顶层） */
    attachTo(host: HTMLElement): void {
        host.appendChild(this.overlay);
    }

    private makeButton(b: SignPopButton): HTMLButtonElement {
        const btn = createButton(`pool-cta${b.ghost ? ' ghost' : ''}`, b.label, () => {
            if (b.disabled) return;
            const ok = b.run ? b.run() : true;
            if (ok === false) return;   // 动作没走成：卡留在原地
            // ⚠️ 先取回调再 close——close() 会置空 this.onResolved（2026-10-30 实测抓出：
            //   原顺序 close→取 done，done 恒 null，调用方的 onResolved 从未被调，队列链断裂）
            const done = this.onResolved;
            this.close();
            done?.();
        });
        if (b.disabled) { btn.disabled = true; btn.classList.add(b.done ? 'done' : 'blocked'); }
        else if (b.blocked) btn.classList.add('blocked');
        if (b.title) btn.title = b.title;
        return btn;
    }

    /** 轻档卡体＝居中戏票（2026-10-30 茄子定稿九改：版式乙、状态挂角色行、两键沿用重档比例） */
    private renderLight(host: HTMLElement, info: SignPopLightInfo): void {
        const wrap = createDiv('sign-pop-light');
        wrap.appendChild(createDiv('sign-pop-script', [info.script]));
        if (info.name) wrap.appendChild(createDiv('sign-pop-name', [info.name]));
        const sub = createDiv('sign-pop-sub');
        if (info.pos) sub.appendChild(createSpan(undefined, info.pos));
        if (info.status) {
            const tone = info.status.mute ? ' mute' : info.status.ok ? '' : ' no';
            sub.appendChild(createSpan(`sign-pop-chip${tone}`, info.status.text));
        }
        if (sub.childElementCount) wrap.appendChild(sub);
        if (info.stats.length) {
            const nums = createDiv('sign-pop-nums');
            info.stats.forEach(st => {
                const cell = document.createElement('span');
                cell.textContent = `${st.label} `;
                const v = document.createElement('b');
                v.textContent = st.value;
                cell.appendChild(v);
                nums.appendChild(cell);
            });
            wrap.appendChild(nums);
        }
        host.appendChild(wrap);
    }
}
